/**
 * Lightweight cookie/consent banner — GDPR + CCPA friendly.
 * Persists user's choice in localStorage. Analytics cookies (PostHog) only
 * fire after "Accept all" or after granular "Accept analytics only" is chosen.
 */
import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "careco24.cookie_consent.v1";

function readConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}
function writeConsent(v) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...v, ts: new Date().toISOString() })); } catch {
        // ignore quota / privacy-mode errors
    }
}

function applyConsent(consent) {
    if (typeof window === "undefined") return;
    if (consent.analytics && window.posthog && typeof window.posthog.opt_in_capturing === "function") {
        window.posthog.opt_in_capturing();
    } else if (!consent.analytics && window.posthog && typeof window.posthog.opt_out_capturing === "function") {
        window.posthog.opt_out_capturing();
    }
}

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const [showDetail, setShowDetail] = useState(false);

    useEffect(() => {
        const existing = readConsent();
        if (!existing) {
            // Show after 800ms so it doesn't fight the initial paint
            const t = setTimeout(() => setVisible(true), 800);
            return () => clearTimeout(t);
        }
        applyConsent(existing);
    }, []);

    function acceptAll() {
        const v = { necessary: true, analytics: true };
        writeConsent(v); applyConsent(v); setVisible(false);
    }
    function rejectAll() {
        const v = { necessary: true, analytics: false };
        writeConsent(v); applyConsent(v); setVisible(false);
    }
    function acceptSelected(analytics) {
        const v = { necessary: true, analytics };
        writeConsent(v); applyConsent(v); setVisible(false);
    }

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-label="Cookie consent"
            data-testid="cookie-consent-banner"
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9998] bg-white border border-stone-200 rounded-2xl shadow-2xl p-5 font-dmsans"
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-emerald-700" />
                    <div className="font-outfit font-semibold text-sm text-stone-900">Cookies &amp; privacy</div>
                </div>
                <button
                    data-testid="cookie-consent-close"
                    onClick={rejectAll}
                    aria-label="Close and reject non-essential"
                    className="text-stone-400 hover:text-stone-700"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed">
                We use strictly-necessary cookies for authentication and session security.
                With your consent, we also use PostHog for product analytics so we can improve
                the experience. Read our <Link to="/privacy" className="text-emerald-700 underline">Privacy Policy</Link>.
            </p>
            {showDetail && (
                <div className="mt-3 space-y-2 text-xs">
                    <label className="flex items-start gap-2">
                        <input type="checkbox" checked disabled className="mt-0.5" />
                        <span className="text-stone-700"><strong>Necessary</strong> — auth, session, security. Always on.</span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            data-testid="cookie-consent-analytics"
                            id="cookie-analytics-cb"
                            className="mt-0.5"
                        />
                        <span className="text-stone-700"><strong>Analytics</strong> — anonymous usage patterns to improve the app.</span>
                    </label>
                </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
                <button
                    data-testid="cookie-consent-accept-all"
                    onClick={acceptAll}
                    className="px-4 h-9 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-outfit font-semibold"
                >
                    Accept all
                </button>
                <button
                    data-testid="cookie-consent-reject-all"
                    onClick={rejectAll}
                    className="px-4 h-9 rounded-full bg-white border border-stone-200 text-stone-700 hover:bg-stone-50 text-xs font-outfit font-medium"
                >
                    Necessary only
                </button>
                {!showDetail ? (
                    <button
                        data-testid="cookie-consent-customize"
                        onClick={() => setShowDetail(true)}
                        className="ml-auto px-3 h-9 text-xs font-outfit text-stone-600 hover:text-stone-900"
                    >
                        Customize
                    </button>
                ) : (
                    <button
                        data-testid="cookie-consent-save"
                        onClick={() => {
                            const cb = document.getElementById("cookie-analytics-cb");
                            acceptSelected(cb?.checked ?? false);
                        }}
                        className="ml-auto px-4 h-9 rounded-full bg-stone-900 text-white text-xs font-outfit font-semibold"
                    >
                        Save choices
                    </button>
                )}
            </div>
        </div>
    );
}
